import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { clearPlatformFeeRateCache } from "../../../utils/platformFee";
import { adminV2Theme as t } from "../theme";

function formatRatePercent(rate) {
  const pct = Number(rate) * 100;
  if (!Number.isFinite(pct)) return "—";
  return `${Number(pct.toFixed(2))}%`;
}

function SummaryChip({ label, value }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: `${t.radius.sm}px`,
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: 11, color: t.colors.textMuted, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: t.colors.text }}>
        {value}
      </Typography>
    </Box>
  );
}

function SettingTile({ title, lines, status, statusColor, onClick, soon }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={soon ? undefined : onClick}
      disabled={soon}
      sx={{
        textAlign: "left",
        cursor: soon ? "default" : "pointer",
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        borderRadius: `${t.radius.md}px`,
        boxShadow: t.shadow.card,
        p: 2,
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        transition: "border-color 0.15s, transform 0.15s",
        fontFamily: t.font.family,
        opacity: soon ? 0.65 : 1,
        "&:hover": soon
          ? {}
          : {
              borderColor: t.colors.primary,
              transform: "translateY(-1px)",
            },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: t.colors.text }}>
          {title}
        </Typography>
        <Chip
          label={status}
          size="small"
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            background: statusColor || t.colors.primarySoft,
            color: t.colors.text,
          }}
        />
      </Box>
      {lines.map((line) => (
        <Typography key={line} sx={{ fontSize: 13, color: t.colors.textMuted }}>
          {line}
        </Typography>
      ))}
      {!soon && (
        <Typography sx={{ fontSize: 12, color: t.colors.primary, fontWeight: 600, mt: "auto" }}>
          Edit →
        </Typography>
      )}
    </Box>
  );
}

function FeeProgramModal({ open, onClose, config, onSaved }) {
  const [enabled, setEnabled] = useState(true);
  const [ratePercent, setRatePercent] = useState("3");
  const [freeWaiverEnabled, setFreeWaiverEnabled] = useState(false);
  const [freeCount, setFreeCount] = useState(3);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !config) return;
    setEnabled(Boolean(config.enabled));
    setRatePercent(String(Number((Number(config.rate ?? 0.03) * 100).toFixed(4))));
    setFreeWaiverEnabled(Boolean(config.free_waiver_enabled));
    setFreeCount(Number(config.free_transaction_count ?? 3));
    setMessage("");
    setError("");
  }, [open, config]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    const pct = Number(ratePercent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setError("Rate must be a number from 0 to 100 (percent).");
      setSaving(false);
      return;
    }
    const rate = pct / 100;
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/fee-program`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enabled,
          rate,
          free_waiver_enabled: freeWaiverEnabled,
          free_transaction_count: freeCount,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
      clearPlatformFeeRateCache();
      setMessage(data.message || "Saved");
      onSaved(data);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={typeof window !== "undefined" && window.innerWidth < 600}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: `${t.radius.lg}px` },
          boxShadow: t.shadow.modal,
          fontFamily: t.font.family,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontFamily: t.font.family }}>
        Platform fee program
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: t.colors.textMuted, fontSize: 13, mb: 2 }}>
          When off, fees do not appear on invoices and are not charged (not shown as 0%).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: t.colors.primary },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: t.colors.primary,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Fee program enabled</Typography>
                <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                  Master on/off for platform fees
                </Typography>
              </Box>
            }
          />

          <TextField
            type="number"
            label="Platform fee rate (%)"
            value={ratePercent}
            onChange={(e) => setRatePercent(e.target.value)}
            disabled={!enabled}
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 100, step: "any" }}
            helperText="Any percent from 0 to 100 (e.g. 1.5, 3, 4.25)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={freeWaiverEnabled}
                onChange={(e) => setFreeWaiverEnabled(e.target.checked)}
                disabled={!enabled}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: t.colors.primary },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: t.colors.primary,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  Free-transaction waiver
                </Typography>
                <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                  First N completed jobs per shop accrue $0 fee
                </Typography>
              </Box>
            }
          />

          <TextField
            type="number"
            label="Free transaction count"
            value={freeCount}
            onChange={(e) => setFreeCount(Math.max(0, Number(e.target.value) || 0))}
            disabled={!enabled || !freeWaiverEnabled}
            size="small"
            inputProps={{ min: 0, step: 1 }}
            helperText="Default 3 when waiver is on"
            fullWidth
          />

          {config?.rate_history?.length > 0 && (
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
                Change history
              </Typography>
              <Box
                sx={{
                  maxHeight: 160,
                  overflowY: "auto",
                  border: `1px solid ${t.colors.border}`,
                  borderRadius: `${t.radius.sm}px`,
                  p: 1.5,
                }}
              >
                {config.rate_history.map((row) => (
                  <Typography
                    key={row.id}
                    sx={{ fontSize: 12, color: t.colors.textMuted, mb: 0.75 }}
                  >
                    {formatRatePercent(row.rate)} — {row.note || "—"} —{" "}
                    {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: t.colors.textMuted }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={{
            textTransform: "none",
            background: t.colors.primary,
            fontWeight: 600,
            "&:hover": { background: t.colors.primaryHover },
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FinanceHubPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feeModalOpen, setFeeModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/fee-program`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      setConfig(await res.json());
    } catch (e) {
      setError(e.message || "Failed to load finance settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  const feeOn = Boolean(config?.enabled);
  const freeOn = Boolean(config?.free_waiver_enabled);
  const freeCount = Number(config?.free_transaction_count ?? 0);

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Business & Finance
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, mb: 2.5, fontSize: 14 }}>
        Current settings at a glance. Open a tile to edit — no page drill-downs.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
        <SummaryChip
          label="Platform fee"
          value={feeOn ? formatRatePercent(config?.rate) : "Off"}
        />
        <SummaryChip
          label="Free transactions"
          value={feeOn && freeOn ? String(freeCount) : feeOn ? "Off" : "—"}
        />
        <SummaryChip label="Fee program" value={feeOn ? "On" : "Off"} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 2,
        }}
      >
        <SettingTile
          title="Platform fee program"
          status={feeOn ? "On" : "Off"}
          statusColor={feeOn ? t.colors.successSoft : t.colors.dangerSoft}
          lines={[
            `Rate: ${feeOn ? formatRatePercent(config?.rate) : "—"}`,
            `Free transactions: ${feeOn && freeOn ? freeCount : "Off"}`,
          ]}
          onClick={() => setFeeModalOpen(true)}
        />
        <SettingTile
          title="Active plans"
          status="Soon"
          statusColor={t.colors.accentSoft}
          lines={["Base tiers & add-ons", "On/off and pricing controls"]}
          soon
        />
        <SettingTile
          title="Collection status"
          status="Soon"
          statusColor={t.colors.accentSoft}
          lines={["Accrued vs collected fees", "Ships with fee collection"]}
          soon
        />
      </Box>

      <FeeProgramModal
        open={feeModalOpen}
        onClose={() => setFeeModalOpen(false)}
        config={config}
        onSaved={(data) => setConfig(data)}
      />
    </Box>
  );
}
